/**
 * Who is signed in, and what they can do.
 *
 * Loaded once at start-up. The API is the authority on permissions — every route
 * re-checks — so this only decides what to *show*, never what to allow.
 */

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import type { Me, Role } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { useAsync } from "./useAsync";

const RANK: Record<Role, number> = { viewer: 0, pm: 1, engineer: 2, admin: 3 };

export interface Session {
  me: Me | null;
  loading: boolean;
  reload: () => void;
  /** The caller's role on a project, or undefined if they have none. */
  roleOn: (projectId: string) => Role | undefined;
  /** Whether they meet a minimum role on a project. Platform admins always do. */
  can: (projectId: string, required: Role) => boolean;
}

const SessionContext = createContext<Session | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, loading, reload } = useAsync(() => endpoints.me(), []);

  const value = useMemo<Session>(() => {
    const me = data ?? null;
    const roles = new Map<string, Role>(
      (me?.memberships ?? []).map((m) => [m.project_id, m.role]),
    );

    const roleOn = (projectId: string) =>
      me?.is_platform_admin ? "admin" : roles.get(projectId);

    return {
      me,
      loading,
      reload,
      roleOn,
      can: (projectId, required) => {
        const held = roleOn(projectId);
        return held !== undefined && RANK[held] >= RANK[required];
      },
    };
  }, [data, loading, reload]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used inside a SessionProvider");
  }
  return context;
}
